import { tierName } from '@/features/account/plan'
import {
  monthsIn,
  offerFor,
  perMonth,
  periodPhrase,
  savingOverMonthly,
  type Catalog,
  type PeriodOffer,
} from '@/features/subscriptions/lib/catalog'
import { formatNPR } from '@/lib/format'
import type { Money } from '@/lib/money'
import type { Entitlement, KnownTier, Plan } from '@/schemas/subscriptions'

/**
 * What the plan picker and checkout draw from the catalogue. No component
 * imports, so the `/subscription` loader can use it without pulling a screen
 * into the entry chunk.
 */

/** A plan whose feature flags name nothing the web can list — said, not left blank. */
export const NOT_LISTED = "What's included isn't listed yet."

export const PAID_TIERS = ['bull', 'shark'] as const satisfies readonly KnownTier[]

/** What a plan card sells for a period: the plan without AI signals, unless that is all there is. */
export const cardPlan = (offer: PeriodOffer): Plan => offer.base ?? offer.withSignals

/** The period the tier sells closest to `periodDays` — the shorter one on a tie. */
export function nearestPeriod(
  catalog: Catalog,
  tier: KnownTier,
  periodDays: number,
): number | null {
  let nearest: number | null = null
  for (const offer of catalog.tiers[tier]) {
    const distance = Math.abs(offer.periodDays - periodDays)
    if (nearest === null || distance < Math.abs(nearest - periodDays)) nearest = offer.periodDays
  }
  return nearest
}

/**
 * The tier's monthly plan of the SAME variant as `plan`: `savingOverMonthly`
 * cannot tell a with-signals plan from its base twin, and pairing them across
 * would sell a saving that does not exist.
 */
function monthlyTwin(catalog: Catalog, tier: KnownTier, plan: Plan): Plan | null {
  const monthly = offerFor(catalog, tier, 30)
  if (monthly === null) return null
  return plan.allowSignal ? monthly.withSignals : monthly.base
}

/** What `plan` keeps against paying monthly for as long, or null. */
export const savingFor = (catalog: Catalog, tier: KnownTier, plan: Plan): Money | null =>
  savingOverMonthly(plan, monthlyTwin(catalog, tier, plan))

const joinAnd = (parts: readonly string[]): string =>
  parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1) ?? ''}`

/**
 * "Yearly saves Rs 1,098 on Sadasya and Rs 2,598 on Guru." — only the tiers
 * whose plan for the period does save; null when none does, so the picker
 * shows no line rather than "saves Rs 0".
 */
export function savingsLine(catalog: Catalog, periodDays: number): string | null {
  const parts = PAID_TIERS.flatMap((tier) => {
    const offer = offerFor(catalog, tier, periodDays)
    const saving = offer === null ? null : savingFor(catalog, tier, cardPlan(offer))
    return saving === null ? [] : [`${formatNPR(saving, { whole: true })} on ${tierName(tier)}`]
  })
  if (parts.length === 0) return null
  const lead = periodDays === 365 ? 'Yearly' : `Paying ${periodPhrase(periodDays)}`
  return `${lead} saves ${joinAnd(parts)}.`
}

/** The run after a card's price: "a year · Rs 208 a month", or just "a month". */
export function priceRun(plan: Plan): string {
  const phrase = periodPhrase(plan.periodDays)
  const months = monthsIn(plan.periodDays)
  const monthly = months !== null && months > 1 ? perMonth(plan) : null
  return monthly === null ? phrase : `${phrase} · ${formatNPR(monthly, { whole: true })} a month`
}

/** How long one purchase lasts: "one month", "3 months", "one year", "7 days". */
export function periodSpan(periodDays: number): string {
  if (periodDays === 30) return 'one month'
  if (periodDays === 365) return 'one year'
  const months = monthsIn(periodDays)
  return months === null ? `${String(periodDays)} days` : `${String(months)} months`
}

/** "Not sold yearly." / "Not sold for 3 months." */
export function notSoldLine(periodDays: number): string {
  if (periodDays === 30) return 'Not sold monthly.'
  if (periodDays === 365) return 'Not sold yearly.'
  return `Not sold for ${periodSpan(periodDays)}.`
}

/**
 * Every plan whose flags a card can show, across every period — fetched up
 * front so switching the period never waits — plus the Free plan's own when
 * the member is on it: `plans/` never lists Free, and `me` is the only place
 * its id comes from.
 */
export function featurePlanIds(catalog: Catalog, me: Entitlement): number[] {
  const ids = PAID_TIERS.flatMap((tier) => catalog.tiers[tier].map((offer) => cardPlan(offer).id))
  if (me.tier === 'free' && me.planId !== null) ids.push(me.planId)
  return [...new Set(ids)]
}
